import React, { useState } from 'react';
import JoyRide, { Step, CallBackProps } from 'react-joyride';
import { useTutorial } from './TutorialContext';

// When a step's target lives behind a collapsed submenu or a closed dropdown, click this
// trigger first to reveal it. Keyed by the step target so it survives feature-flag
// filtering (which removes steps and shifts indices). Clicks are only fired when the
// target isn't already on screen, so it's safe to call on both next and prev navigation.
const REVEAL_TRIGGER: Record<string, string> = {
	'.journey-profile-menu-item': '.my-journey-menu-item',
	'.portal-menu-item': '.my-journey-menu-item',
	'.my-enquiries-menu-item': '.my-journey-menu-item',
	'.profile-menu-item': '.profile-icon-btn',
	'.my-account-menu-item': '.profile-icon-btn',
	'.faq-menu-item': '.profile-icon-btn'
};

// On mobile, sidebar items live inside the drawer while page content and the profile
// dropdown need the drawer closed. Maps a step target to the drawer state it requires.
const MOBILE_DRAWER_OPEN: Record<string, boolean> = {
	'.menu-drawer-btn': false,
	'.wallet-menu-item': true,
	'.firstWalletCategory': false,
	'.uploadDocBtn': false,
	'.verification-menu-item': true,
	'.journey-profile-menu-item': true,
	'.portal-menu-item': true,
	'.my-enquiries-menu-item': true,
	'.shared-wallet-menu-item': true,
	'.profile-icon-btn': false,
	'.profile-menu-item': false,
	'.my-account-menu-item': false,
	'.faq-menu-item': false
};

const TutorialWizard = ({
	isMobile,
	setDrawerOpen,
	steps
}: {
	isMobile: boolean;
	setDrawerOpen?: any;
	steps: Step[];
}) => {
	const [stepIndex, setStepIndex] = useState(0);
	const { tourActive } = useTutorial();
	const [run, setRun] = useState(tourActive ? tourActive : false);

	// Set up the DOM for the step that is about to be shown: open/close the mobile drawer
	// and reveal any collapsed submenu or dropdown the step's target lives in.
	const prepareForStep = (step?: Step) => {
		if (!step) return;
		const target = typeof step.target === 'string' ? step.target : '';
		if (!target || target === 'body') return;

		if (isMobile && target in MOBILE_DRAWER_OPEN) {
			setDrawerOpen?.(MOBILE_DRAWER_OPEN[target]);
		}

		const trigger = REVEAL_TRIGGER[target];
		if (trigger && !document.querySelector(target)) {
			(document.querySelector(trigger) as HTMLElement | null)?.click();
		}
	};

	const handleJoyRideCallBack = (data: CallBackProps) => {
		const { index, type, status, action } = data;

		if (status === 'finished' || status === 'skipped' || action === 'close') {
			setStepIndex(0);
			setRun(false);
			return;
		}

		if (type === 'step:after' && action === 'next') {
			prepareForStep(steps[index + 1]);
			setStepIndex(index + 1);
		}

		if (type === 'step:after' && action === 'prev') {
			prepareForStep(steps[index - 1]);
			setStepIndex(index - 1);
		}
	};

	return (
		<JoyRide
			steps={steps}
			run={run}
			callback={handleJoyRideCallBack}
			continuous
			scrollToFirstStep
			showProgress
			showSkipButton
			disableOverlayClose
			stepIndex={stepIndex}
			scrollOffset={100}
			locale={{
				last: 'OK'
			}}
			styles={{
				options: {
					zIndex: 10000
				},
				buttonNext: {
					backgroundColor: 'rgb(0, 28, 72)',
					boxShadow:
						'0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)'
					// color: '#fff'
				},
				buttonBack: {
					//	color: '#ccc'
				},
				buttonClose: {
					//	color: '#999'
				},
				tooltipContainer: {},
				tooltip: {
					borderRadius: '10px',
					padding: '16px'
				},
				tooltipTitle: {
					fontSize: '18px',
					fontWeight: 'bold'
				},
				tooltipContent: {
					fontSize: '15px'
				}
			}}
		></JoyRide>
	);
};

export default TutorialWizard;
